/**
 * Comment Actions Menu Component
 * 
 * Provides delete/hide/share actions for comments across all content types:
 * - Posts (Feed, Profile)
 * - Soundstage Clips
 * - Videos
 * - Featured Content
 * 
 * Permissions:
 * - Comment author: Can delete their own comment, share
 * - Post/content owner: Can hide/remove any comment on their content
 * - Platform owner/admin: Can remove any comment
 * - Other users: Can only share (read-only)
 */

import { useState } from 'react';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { 
  MoreHorizontal, 
  Trash2, 
  EyeOff, 
  Eye,
  Share2, 
  Flag, 
  Copy,
  Link as LinkIcon,
  Loader2
} from 'lucide-react';

export default function CommentActionsMenu({ 
  comment, 
  contentOwnerId,  // user_id of the post/reel/video owner
  contentType = 'post',  // 'post', 'reel', 'video', 'interview'
  onDelete,  // Callback after successful delete
  onHide,    // Callback after hide/unhide
  className = ''
}) {
  const { user, getAuthHeaders, isAuthenticated } = useAuth();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [hiding, setHiding] = useState(false);
  const [isHidden, setIsHidden] = useState(comment?.is_hidden || false);

  // Permission checks
  const isCommentAuthor = user?.user_id === comment?.user_id;
  const isContentOwner = user?.user_id === contentOwnerId;
  const isPlatformOwner = user?.email === 'itzchopbusiness1@gmail.com' || 
                          user?.is_platform_super_admin === true;
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  
  const canDelete = isCommentAuthor || isContentOwner || isPlatformOwner || isAdmin;
  const canHide = isContentOwner || isPlatformOwner || isAdmin;
  const canReport = isAuthenticated && !isCommentAuthor;

  // Get the correct endpoint based on content type
  const getEndpointPrefix = () => {
    switch (contentType) {
      case 'reel':
      case 'clip':
        return 'reel-comments';
      case 'video':
        return 'video-comments';
      default:
        return 'post-comments';
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const endpoint = `${API}/${getEndpointPrefix()}/${comment.comment_id}`;
      const res = await fetch(endpoint, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (res.ok) {
        toast.success('Comment deleted');
        setShowDeleteConfirm(false);
        if (onDelete) onDelete(comment.comment_id);
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to delete comment');
      }
    } catch (err) {
      console.error('Delete comment error:', err);
      toast.error('Failed to delete comment');
    } finally {
      setDeleting(false);
    }
  };

  const handleHide = async () => {
    setHiding(true);
    try {
      const endpoint = `${API}/${getEndpointPrefix()}/${comment.comment_id}/hide`;
      const res = await fetch(endpoint, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });

      if (res.ok) {
        const data = await res.json();
        setIsHidden(data.hidden);
        toast.success(data.hidden ? 'Comment hidden' : 'Comment visible');
        if (onHide) onHide(comment.comment_id, data.hidden);
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to hide comment');
      }
    } catch (err) {
      console.error('Hide comment error:', err);
      toast.error('Failed to hide comment');
    } finally {
      setHiding(false);
    }
  };

  const handleShare = async () => {
    try {
      // Try to get share link from API
      const endpoint = `${API}/${getEndpointPrefix()}/${comment.comment_id}/share`;
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      let shareUrl = window.location.href;
      
      if (res.ok) {
        const data = await res.json();
        shareUrl = data.share_url || shareUrl;
      }

      // Try native share first
      if (navigator.share) {
        await navigator.share({
          title: 'Comment',
          text: comment.content?.substring(0, 100),
          url: shareUrl
        });
        toast.success('Shared successfully');
      } else {
        // Fallback to clipboard
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard');
      }
    } catch (err) {
      // If share was cancelled or failed, try clipboard
      try {
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Link copied to clipboard');
      } catch (clipboardErr) {
        toast.error('Failed to share');
      }
    }
  };

  const handleReport = async () => {
    try {
      const endpoint = `${API}/${getEndpointPrefix()}/${comment.comment_id}/report`;
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'inappropriate' })
      });

      if (res.ok) {
        toast.success('Comment reported. We\'ll review it shortly.');
      } else {
        toast.error('Failed to report comment');
      }
    } catch (err) {
      toast.error('Failed to report comment');
    }
  };

  // If user can't do anything, don't show the menu
  if (!canDelete && !canHide && !isAuthenticated) {
    return null;
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className={`h-8 w-8 p-0 ${className}`}
            data-testid={`comment-actions-${comment.comment_id}`}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {/* Share - Available to everyone */}
          <DropdownMenuItem onClick={handleShare} className="cursor-pointer">
            <Share2 className="h-4 w-4 mr-2" />
            Share comment
          </DropdownMenuItem>

          <DropdownMenuItem onClick={() => {
            try { navigator.clipboard.writeText(comment.content || '').catch(() => {}); } catch {}
            toast.success('Comment text copied');
          }} className="cursor-pointer">
            <Copy className="h-4 w-4 mr-2" />
            Copy text
          </DropdownMenuItem>

          {/* Hide - Only for content owner/admin */}
          {canHide && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={handleHide} 
                disabled={hiding}
                className="cursor-pointer"
              >
                {hiding ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : isHidden ? (
                  <Eye className="h-4 w-4 mr-2" />
                ) : (
                  <EyeOff className="h-4 w-4 mr-2" />
                )}
                {isHidden ? 'Show comment' : 'Hide comment'}
              </DropdownMenuItem>
            </>
          )}

          {/* Delete - Comment author, content owner, or admin */}
          {canDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => setShowDeleteConfirm(true)}
                className="cursor-pointer text-red-500 focus:text-red-500"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {isCommentAuthor ? 'Delete comment' : 'Remove comment'}
              </DropdownMenuItem>
            </>
          )}

          {/* Report - Available to authenticated non-authors */}
          {canReport && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={handleReport}
                className="cursor-pointer text-orange-500 focus:text-orange-500"
              >
                <Flag className="h-4 w-4 mr-2" />
                Report comment
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {isCommentAuthor ? 'Delete your comment?' : 'Remove this comment?'}
            </DialogTitle>
            <DialogDescription>
              {isCommentAuthor 
                ? 'This will permanently delete your comment. This action cannot be undone.'
                : 'This will remove the comment from your post. The comment author will be notified.'
              }
            </DialogDescription>
          </DialogHeader>
          
          {/* Preview of comment to be deleted */}
          <div className="p-3 rounded-lg bg-muted/50 text-sm">
            <p className="font-medium mb-1">@{comment.author_handle || comment.handle || 'user'}</p>
            <p className="text-muted-foreground line-clamp-2">{comment.content}</p>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteConfirm(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                isCommentAuthor ? 'Delete' : 'Remove'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Hidden Comment Placeholder
 * Shows a placeholder for hidden comments (visible only to content owner/admin)
 */
export function HiddenCommentPlaceholder({ comment, onUnhide }) {
  const { user } = useAuth();
  
  const isContentOwner = user?.user_id === comment?.content_owner_id;
  const isPlatformOwner = user?.email === 'itzchopbusiness1@gmail.com';
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  
  if (!isContentOwner && !isPlatformOwner && !isAdmin) {
    return null;  // Don't show hidden comments to regular users
  }

  return (
    <div className="p-3 rounded-lg bg-muted/30 border border-dashed border-muted-foreground/30 text-sm">
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground flex items-center gap-2">
          <EyeOff className="h-4 w-4" />
          This comment is hidden from public view
        </span>
        {onUnhide && (
          <Button variant="ghost" size="sm" onClick={onUnhide}>
            <Eye className="h-4 w-4 mr-1" />
            Show
          </Button>
        )}
      </div>
      <p className="mt-2 text-muted-foreground/70 line-clamp-1">
        "{comment.content}"
      </p>
    </div>
  );
}
